// Story 7.1 AC5 — Timeline de mudanças do plano (preço + entitlements).
// Extraído de pages/plans/[id].tsx HistoryTab; refatorado pra usar
// formatBRDateTime (disciplina de timezone).

import { formatBRDateTime } from '@control-center/shared';

import { usePlanEntitlementsHistory, usePlanPriceHistory } from '../../hooks/use-plan-history';

interface PlanHistoryViewerProps {
  planId: string;
}

function formatBRL(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function PlanHistoryViewer({ planId }: PlanHistoryViewerProps): JSX.Element {
  const priceH = usePlanPriceHistory(planId);
  const entH = usePlanEntitlementsHistory(planId);

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <section className="rounded-[14px] border border-line bg-panel p-5">
        <h3 className="m-0 mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Mudanças de preço
        </h3>
        {priceH.isLoading ? (
          <div className="text-[12px] text-muted">Carregando…</div>
        ) : priceH.data && priceH.data.items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {priceH.data.items.map((h) => (
              <li
                key={h.id}
                className="rounded-[8px] border border-line bg-panel-2 p-3 text-[13px]"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase text-muted-2">{h.field}</span>
                  <time className="text-[11px] text-muted-2" dateTime={h.changedAt}>
                    {formatBRDateTime(h.changedAt)}
                  </time>
                </div>
                <div className="mt-1">
                  <span className="text-muted line-through">{formatBRL(h.oldPrice)}</span>
                  <span className="mx-2 text-muted-2">→</span>
                  <span className="font-medium text-ink">{formatBRL(h.newPrice)}</span>
                </div>
                {h.changedByUser && (
                  <div className="mt-1 text-[11px] text-muted-2">por {h.changedByUser.name}</div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] text-muted">Sem mudanças de preço.</p>
        )}
      </section>

      <section className="rounded-[14px] border border-line bg-panel p-5">
        <h3 className="m-0 mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Mudanças de entitlements
        </h3>
        {entH.isLoading ? (
          <div className="text-[12px] text-muted">Carregando…</div>
        ) : entH.data && entH.data.items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {entH.data.items.map((h) => (
              <li
                key={h.id}
                className="rounded-[8px] border border-line bg-panel-2 p-3 text-[13px]"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`rounded-[6px] px-2 py-[2px] text-[11px] font-medium uppercase ${
                      h.action === 'added'
                        ? 'bg-good/10 text-good'
                        : h.action === 'removed'
                          ? 'bg-bad/10 text-bad'
                          : 'bg-warn/10 text-warn'
                    }`}
                  >
                    {h.action}
                  </span>
                  <time className="text-[11px] text-muted-2" dateTime={h.changedAt}>
                    {formatBRDateTime(h.changedAt)}
                  </time>
                </div>
                <div className="mt-2 text-ink">{h.saasProduct.name}</div>
                {h.changedByUser && (
                  <div className="mt-1 text-[11px] text-muted-2">por {h.changedByUser.name}</div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] text-muted">Sem mudanças de entitlements.</p>
        )}
      </section>
    </div>
  );
}
